import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import './AdminPhotographers.css';

const AdminPhotographers = () => {
  usePageTitle('Manage Photographers');
  const navigate = useNavigate();
  const { isFounder } = useAuth();
  const { success, error: showError } = useToast();
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPhotographer, setEditingPhotographer] = useState(null);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_arabic: '',
    bio: '',
    phone: '',
    whatsapp: '',
    instagram: '',
    facebook: '',
    order: 0,
    photo: null,
  });
  const [packageFormData, setPackageFormData] = useState({
    name: '',
    photos_count: 0,
    videos_count: 0,
    price: '',
    description: '',
    order: 0,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isFounder) {
      navigate('/admin');
      return;
    }
    fetchPhotographers();
  }, [isFounder, navigate]);

  const fetchPhotographers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/photographers');
      const photographersData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data && Array.isArray(response.data.data) 
          ? response.data.data 
          : []);
      setPhotographers(photographersData);
    } catch (error) {
      console.error('Error fetching photographers:', error);
      showError('Error fetching photographers');
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (photographer) => {
    if (photographer.photo_url) {
      return `${photographer.photo_url}?t=${Date.now()}`;
    }
    if (photographer.photo_path) {
      if (photographer.photo_path.startsWith('http')) {
        return `${photographer.photo_path}?t=${Date.now()}`;
      }
      return `https://aliensteam.com/storage/${photographer.photo_path}?t=${Date.now()}`;
    }
    return null;
  };

  const handleOpenModal = (photographer = null) => {
    if (photographer) {
      setEditingPhotographer(photographer);
      setFormData({
        name: photographer.name || '',
        name_arabic: photographer.name_arabic || '',
        bio: photographer.bio || '',
        phone: photographer.phone || '',
        whatsapp: photographer.whatsapp || '',
        instagram: photographer.instagram || '',
        facebook: photographer.facebook || '',
        order: photographer.order || 0,
        photo: null,
      });
      setImagePreview(getPhotoUrl(photographer));
    } else {
      setEditingPhotographer(null);
      setFormData({
        name: '',
        name_arabic: '',
        bio: '',
        phone: '',
        whatsapp: '',
        instagram: '',
        facebook: '',
        order: 0,
        photo: null,
      });
      setImagePreview(null);
    }
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, photo: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('name_arabic', formData.name_arabic || '');
      submitData.append('bio', formData.bio || '');
      submitData.append('phone', formData.phone || '');
      submitData.append('whatsapp', formData.whatsapp || '');
      submitData.append('instagram', formData.instagram || '');
      submitData.append('facebook', formData.facebook || '');
      submitData.append('order', formData.order || 0);
      if (formData.photo) {
        submitData.append('photo', formData.photo);
      }

      if (editingPhotographer) {
        submitData.append('_method', 'PUT');
        await api.post(`/photographers/${editingPhotographer.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success('Photographer updated successfully!');
      } else {
        await api.post('/photographers', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success('Photographer created successfully!');
      }

      setShowModal(false);
      fetchPhotographers();
    } catch (error) {
      console.error('Error saving photographer:', error);
      showError(error.response?.data?.message || 'Error saving photographer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (photographerId) => {
    if (!window.confirm('Are you sure you want to delete this photographer? All packages will also be deleted.')) {
      return;
    }

    try {
      await api.delete(`/photographers/${photographerId}`);
      success('Photographer deleted successfully!');
      fetchPhotographers();
    } catch (error) {
      console.error('Error deleting photographer:', error);
      showError(error.response?.data?.message || 'Error deleting photographer');
    }
  };

  // =============== Package Management ===============

  const handleOpenPackageModal = (photographer, pkg = null) => {
    setSelectedPhotographer(photographer);
    if (pkg) {
      setEditingPackage(pkg);
      setPackageFormData({
        name: pkg.name || '',
        photos_count: pkg.photos_count || 0,
        videos_count: pkg.videos_count || 0,
        price: pkg.price || '',
        description: pkg.description || '',
        order: pkg.order || 0,
      });
    } else {
      setEditingPackage(null);
      setPackageFormData({
        name: '',
        photos_count: 0,
        videos_count: 0,
        price: '',
        description: '',
        order: 0,
      });
    }
    setShowPackageModal(true);
  };

  const handleSubmitPackage = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        name: packageFormData.name,
        photos_count: parseInt(packageFormData.photos_count) || 0,
        videos_count: parseInt(packageFormData.videos_count) || 0,
        price: parseFloat(packageFormData.price) || 0,
        description: packageFormData.description || '',
        order: parseInt(packageFormData.order) || 0,
      };

      if (editingPackage) {
        await api.put(`/photographers/${selectedPhotographer.id}/packages/${editingPackage.id}`, data);
        success('Package updated successfully!');
      } else {
        await api.post(`/photographers/${selectedPhotographer.id}/packages`, data);
        success('Package added successfully!');
      }

      setShowPackageModal(false);
      fetchPhotographers();
    } catch (error) {
      console.error('Error saving package:', error);
      showError(error.response?.data?.message || 'Error saving package');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePackage = async (photographerId, packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) {
      return;
    }

    try {
      await api.delete(`/photographers/${photographerId}/packages/${packageId}`);
      success('Package deleted successfully!');
      fetchPhotographers();
    } catch (error) {
      console.error('Error deleting package:', error);
      showError(error.response?.data?.message || 'Error deleting package');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ar-EG').format(price);
  };

  if (loading) {
    return (
      <div className="admin-photographers-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading photographers...</p>
      </div>
    );
  }

  return (
    <div className="admin-photographers-container">
      <div className="admin-photographers-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Dashboard
        </button>
        <h1>📸 Manage Photographers</h1>
        <button className="btn-primary" onClick={() => handleOpenModal()}>
          ➕ Add New Photographer
        </button>
      </div>

      <div className="photographers-admin-grid">
        {photographers.length === 0 ? (
          <div className="no-photographers-admin">
            <div className="no-data-icon">📷</div>
            <p>No photographers found. Add your first photographer!</p>
          </div>
        ) : (
          photographers.map((photographer) => (
            <div key={photographer.id} className="photographer-admin-card">
              <div className="photographer-admin-header">
                <div className="photographer-admin-photo">
                  {getPhotoUrl(photographer) ? (
                    <img
                      src={getPhotoUrl(photographer)}
                      alt={photographer.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="photo-placeholder" style={{ display: getPhotoUrl(photographer) ? 'none' : 'flex' }}>
                    📷
                  </div>
                </div>
                <div className="photographer-admin-info">
                  <h3>{photographer.name}</h3>
                  {photographer.name_arabic && (
                    <p className="name-arabic">{photographer.name_arabic}</p>
                  )}
                  <div className="photographer-contacts-admin">
                    {photographer.phone && <span>📞 {photographer.phone}</span>}
                    {photographer.whatsapp && <span>💬 WhatsApp</span>}
                    {photographer.instagram && <span>📷 Instagram</span>}
                  </div>
                </div>
                <div className="photographer-admin-actions">
                  <button className="btn-edit" onClick={() => handleOpenModal(photographer)}>
                    ✏️
                  </button>
                  <button className="btn-delete" onClick={() => handleDelete(photographer.id)}>
                    🗑️
                  </button>
                </div>
              </div>

              <div className="packages-section-admin">
                <div className="packages-header-admin">
                  <h4>📦 Packages ({photographer.active_packages?.length || 0})</h4>
                  <button 
                    className="btn-add-package"
                    onClick={() => handleOpenPackageModal(photographer)}
                  >
                    ➕ Add Package
                  </button>
                </div>
                
                {photographer.active_packages && photographer.active_packages.length > 0 ? (
                  <div className="packages-list-admin">
                    {photographer.active_packages.map((pkg) => (
                      <div key={pkg.id} className="package-item-admin">
                        <div className="package-info-admin">
                          <span className="package-name-admin">{pkg.name}</span>
                          <div className="package-details-admin">
                            <span>📷 {pkg.photos_count} photos</span>
                            <span>🎬 {pkg.videos_count} videos</span>
                            <span className="package-price-admin">{formatPrice(pkg.price)} EGP</span>
                          </div>
                        </div>
                        <div className="package-actions-admin">
                          <button 
                            className="btn-edit-small"
                            onClick={() => handleOpenPackageModal(photographer, pkg)}
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn-delete-small"
                            onClick={() => handleDeletePackage(photographer.id, pkg.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-packages-text">No packages yet</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Photographer Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content photographer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>{editingPhotographer ? 'Edit Photographer' : 'Add New Photographer'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name (English) *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Mohamed Ragab"
                  />
                </div>
                <div className="form-group">
                  <label>Name (Arabic)</label>
                  <input
                    type="text"
                    value={formData.name_arabic}
                    onChange={(e) => setFormData({ ...formData, name_arabic: e.target.value })}
                    placeholder="محمد رجب"
                    dir="rtl"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows="3"
                  placeholder="A short bio about the photographer..."
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="01234567890"
                  />
                </div>
                <div className="form-group">
                  <label>WhatsApp</label>
                  <input
                    type="text"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    placeholder="201234567890"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Instagram</label>
                  <input
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    placeholder="username or full URL"
                  />
                </div>
                <div className="form-group">
                  <label>Facebook</label>
                  <input
                    type="text"
                    value={formData.facebook}
                    onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                    placeholder="username or full URL"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>Photo {!editingPhotographer && '*'}</label>
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  required={!editingPhotographer}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingPhotographer ? 'Update Photographer' : 'Create Photographer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Package Modal */}
      {showPackageModal && (
        <div className="modal-overlay" onClick={() => setShowPackageModal(false)}>
          <div className="modal-content package-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPackageModal(false)}>×</button>
            <h2>
              {editingPackage ? 'Edit Package' : 'Add New Package'}
              <span className="modal-subtitle">for {selectedPhotographer?.name}</span>
            </h2>
            <form onSubmit={handleSubmitPackage}>
              <div className="form-group">
                <label>Package Name *</label>
                <input
                  type="text"
                  value={packageFormData.name}
                  onChange={(e) => setPackageFormData({ ...packageFormData, name: e.target.value })}
                  required
                  placeholder="e.g., Basic Package, Premium Package"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>📷 Number of Photos *</label>
                  <input
                    type="number"
                    min="0"
                    value={packageFormData.photos_count}
                    onChange={(e) => setPackageFormData({ ...packageFormData, photos_count: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>🎬 Number of Videos *</label>
                  <input
                    type="number"
                    min="0"
                    value={packageFormData.videos_count}
                    onChange={(e) => setPackageFormData({ ...packageFormData, videos_count: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>💰 Price (EGP) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={packageFormData.price}
                    onChange={(e) => setPackageFormData({ ...packageFormData, price: e.target.value })}
                    required
                    placeholder="1000"
                  />
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={packageFormData.order}
                    onChange={(e) => setPackageFormData({ ...packageFormData, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={packageFormData.description}
                  onChange={(e) => setPackageFormData({ ...packageFormData, description: e.target.value })}
                  rows="2"
                  placeholder="Additional details about this package..."
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowPackageModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingPackage ? 'Update Package' : 'Add Package'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPhotographers;
